import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useSelector } from 'react-redux';
import DialPhone, { PhoneUserIdentity } from '../../connection/phone';
import { GreenroomState } from '../../stores/types/types';

type DialStatus = 'idle' | 'dialing' | 'error';

interface Props {
	identity: PhoneUserIdentity & { first_name?: string; last_name?: string; phone?: string };
	onClose: () => void;
	/** Called when the dial-out API call succeeds (before SIP stream arrives). */
	onCallStarted?: () => void;
}

const PAD_ROWS = [
	[{ key: '1', sub: '' }, { key: '2', sub: 'ABC' }, { key: '3', sub: 'DEF' }],
	[{ key: '4', sub: 'GHI' }, { key: '5', sub: 'JKL' }, { key: '6', sub: 'MNO' }],
	[{ key: '7', sub: 'PQRS' }, { key: '8', sub: 'TUV' }, { key: '9', sub: 'WXYZ' }],
	[{ key: '*', sub: '' }, { key: '0', sub: '+' }, { key: '#', sub: '' }],
];

export default function FeedPhoneDialer({ identity, onClose, onCallStarted }: Props) {
	const token = useSelector((state: GreenroomState) => state.SessionReducer.token);
	const meetingId = useSelector(
		(state: GreenroomState) => state.SessionReducer.meeting?.meeting_id,
	);

	// Look up the mask phone directly — bypasses the use_profile_masks display
	// gate in useProfile so the number populates regardless of that feature flag.
	const activeMasks = useSelector(
		(state: GreenroomState) => state.MasksReducer.activeMasksEntity?.entityValue,
	);
	const profileMasks = useSelector((state: GreenroomState) => state.MasksReducer.profileMasks);
	// Start with whatever we can resolve synchronously; the effect below keeps
	// this up to date in case the Redux mask data arrives after first render.
	const resolveMaskPhone = () => {
		if (!identity.email) return undefined;
		const maskId = activeMasks?.[identity.email];
		return maskId != null ? profileMasks?.[maskId]?.phone ?? undefined : undefined;
	};

	const [phoneNumber, setPhoneNumber] = useState(() => resolveMaskPhone() || identity.phone || '');
	const [status, setStatus] = useState<DialStatus>('idle');
	const [errorMessage, setErrorMessage] = useState('');
	// Track whether the user has typed anything so we never overwrite their input.
	const touched = useRef(false);

	const displayName =
		[identity.first_name, identity.last_name].filter(Boolean).join(' ') ||
		identity.email ||
		'Unknown';

	const initials = (
		[identity.first_name?.[0], identity.last_name?.[0]].filter(Boolean).join('') ||
		identity.email?.[0] ||
		'?'
	).toUpperCase();

	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') onClose();
		};
		window.addEventListener('keydown', onKey);
		return () => window.removeEventListener('keydown', onKey);
	}, [onClose]);

	// Populate from mask data whenever Redux updates — handles cases where the
	// socket event from the last save arrives after this component first mounts.
	useEffect(() => {
		if (touched.current) return;
		const phone = resolveMaskPhone() || identity.phone || '';
		if (phone) setPhoneNumber(phone);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeMasks, profileMasks, identity.email, identity.phone]);

	const appendDigit = useCallback((digit: string) => {
		touched.current = true;
		setPhoneNumber((prev) => prev + digit);
	}, []);

	const backspace = useCallback(() => {
		touched.current = true;
		setPhoneNumber((prev) => prev.slice(0, -1));
	}, []);

	const handleDial = async () => {
		if (!phoneNumber.trim() || !token || !meetingId || status === 'dialing') return;
		setStatus('dialing');
		setErrorMessage('');
		const result = await DialPhone(token, phoneNumber.trim(), meetingId, identity);
		if (result?.message) {
			setStatus('error');
			setErrorMessage(result.message);
		} else {
			onCallStarted?.();
			onClose();
		}
	};

	return ReactDOM.createPortal(
		<div className="phone-dialer-modal-backdrop" onClick={onClose}>
			<div className="phone-dialer-modal" onClick={(e) => e.stopPropagation()}>

				{/* User info */}
				<div className="phone-dialer-modal__header">
					<div className="phone-dialer-modal__avatar">
						{identity.poster
							? <img src={identity.poster} alt={displayName} />
							: <span>{initials}</span>
						}
					</div>
					<div className="phone-dialer-modal__user-info">
						<span className="phone-dialer-modal__name">{displayName}</span>
						{identity.email && (
							<span className="phone-dialer-modal__email">{identity.email}</span>
						)}
					</div>
				</div>

				{/* Number display */}
				<div className="phone-dialer-modal__screen">
					<input
						className="phone-dialer-modal__number-input"
						type="tel"
						placeholder="+1 555 000 0000"
						value={phoneNumber}
						onChange={(e) => { touched.current = true; setPhoneNumber(e.target.value); }}
						disabled={status === 'dialing'}
						autoFocus
					/>
					{phoneNumber && (
						<button
							className="phone-dialer-modal__backspace"
							onClick={backspace}
							type="button"
							tabIndex={-1}
						>
							⌫
						</button>
					)}
				</div>

				{/* Number pad */}
				<div className="phone-dialer-modal__pad">
					{PAD_ROWS.map((row, i) => (
						<div key={i} className="phone-dialer-modal__pad-row">
							{row.map(({ key, sub }) => (
								<button
									key={key}
									className="phone-dialer-modal__pad-key"
									onClick={() => appendDigit(key)}
									type="button"
									tabIndex={-1}
								>
									<span className="phone-dialer-modal__pad-digit">{key}</span>
									{sub && <span className="phone-dialer-modal__pad-sub">{sub}</span>}
								</button>
							))}
						</div>
					))}
				</div>

				{status === 'error' && (
					<span className="phone-dialer-modal__error">{errorMessage}</span>
				)}

				{/* Actions */}
				<div className="phone-dialer-modal__actions">
					<button className="phone-dialer-modal__cancel" onClick={onClose} type="button">
						Cancel
					</button>
					<button
						className="phone-dialer-modal__call"
						onClick={handleDial}
						disabled={status === 'dialing' || !phoneNumber.trim()}
						type="button"
					>
						{status === 'dialing' ? 'Calling…' : 'Call'}
					</button>
				</div>
			</div>
		</div>,
		document.body,
	);
}
